import { Button, Modal, Spinner, Typography } from "@/components/atoms";
import { ClientBookingCancellationPolicy } from "@/redux/apis/clientBooking/types";

interface CancelBookingModalProps {
  open: boolean;
  /** Display id shown in the copy, e.g. "J000123". */
  displayJobId: string;
  /** Provider cancellation policy + fees (from /v1/cust/data). Omitted while unavailable. */
  policy?: ClientBookingCancellationPolicy;
  /** True while the policy is still being fetched — shows a spinner instead of a blank pop-in. */
  policyLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Confirmation gate for cancelling a booking. Mirrors the mobile CancelModal: the client sees the
 * provider's cancellation policy + fee schedule before confirming (a cancellation may incur a fee and
 * notify the driver/operator). "Keep Booking" dismisses; "Cancel Booking" fires the cancel request.
 */
export const CancelBookingModal = ({
  open,
  displayJobId,
  policy,
  policyLoading = false,
  onClose,
  onConfirm,
  loading = false,
}: CancelBookingModalProps) => (
  <Modal
    open={open}
    onClose={loading ? () => undefined : onClose}
    variant="web-small"
    className="sm:w-[440px]"
    title={`Cancel booking ${displayJobId}?`}
    description="This booking will be cancelled and can't be undone. Review the cancellation policy below before confirming."
    renderButtons={() => (
      <>
        <Button variant="tertiary" size="lg" onClick={onClose} disabled={loading} className="flex-1">
          Keep Booking
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 bg-danger hover:bg-danger-dark"
          startIcon={loading ? <Spinner variant="light" /> : undefined}
        >
          Cancel Booking
        </Button>
      </>
    )}
  >
    <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-3">
      {policyLoading && !policy ? (
        <div className="flex items-center gap-2">
          <Spinner className="!m-0 h-4 w-4" />
          <Typography variant="small-regular" className="text-neutral-dark-gray">
            Loading cancellation policy…
          </Typography>
        </div>
      ) : policy ? (
        <>
          {policy.text && (
            <Typography variant="small-regular" className="mb-2 block text-neutral-dark-gray">
              {policy.text}
            </Typography>
          )}
          {!!policy.fees?.length && (
            <>
              <Typography variant="small" className="mb-1 block">
                Cancellation Fees
              </Typography>
              <ul className="flex flex-col gap-0.5">
                {policy.fees.map((fee, index) => (
                  <li key={index}>
                    <Typography variant="small-regular" className="text-neutral-dark-gray">
                      {fee}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <Typography variant="small-regular" className="text-neutral-dark-gray">
          Cancellation fees may apply depending on how close to pickup you cancel.
        </Typography>
      )}
    </div>
  </Modal>
);
